/**
 * Wake lab-connector on the operator PC via the local launcher (127.0.0.1).
 * The hosted API cannot spawn OS processes; the browser calls localhost instead.
 */

const DEFAULT_LAUNCHER_BASE =
  typeof process !== 'undefined' && process.env?.REACT_APP_CONNECTOR_LAUNCHER_URL
    ? process.env.REACT_APP_CONNECTOR_LAUNCHER_URL.replace(/\/$/, '')
    : 'http://127.0.0.1:17871';

async function launcherFetch(path, options = {}) {
  const url = `${DEFAULT_LAUNCHER_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    mode: 'cors',
    headers: { Accept: 'application/json', ...(options.headers || {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || `Launcher HTTP ${res.status}`);
    err.code = data.error || 'launcher_http_error';
    throw err;
  }
  return data;
}

/** @returns {Promise<{ok: boolean, connector_running?: boolean, reason?: string}>} */
export async function probeLocalLauncher() {
  try {
    const data = await launcherFetch('/health', { method: 'GET' });
    return { ok: true, connector_running: Boolean(data.connector_running), raw: data };
  } catch {
    return { ok: false, reason: 'launcher_offline' };
  }
}

/**
 * Ask the local launcher to start lab-connector (idempotent).
 * @returns {Promise<{ok: boolean, started?: boolean, already_running?: boolean, reason?: string}>}
 */
export async function wakeLocalConnector() {
  try {
    const data = await launcherFetch('/start-connector', { method: 'POST' });
    return {
      ok: Boolean(data.ok),
      started: Boolean(data.started),
      already_running: Boolean(data.already_running),
      pid: data.pid,
      message: data.message,
    };
  } catch (e) {
    return { ok: false, reason: e.code || 'launcher_offline', message: e.message };
  }
}

/**
 * Poll launcher + cloud connector online with backoff (after wake).
 */
export async function waitForConnectorOnline(isOnline, { maxAttempts = 20, intervalMs = 1500 } = {}) {
  for (let i = 0; i < maxAttempts; i += 1) {
    if (isOnline()) return true;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return isOnline();
}

export function launcherInstallHint() {
  return 'Use Cyber Range → Operator stack → Download installer (env vars are filled in automatically).';
}
